import {captureTokenAuthError, sweetAPI} from 'app/store/sweet-api';
import {
  createTagsListFromResponse,
  showMessages,
} from 'app/store/sweet-api/utils';
import {LANES_API} from 'app/urls';
import {UNLIMITED_PAGE_SIZE} from 'app/utils/constants';
import {gt} from 'app/utils/i18n';

import {CreateLaneSerializedType} from './types';

import {CommonFetchParams} from '../utils/request';

import type {LaneSerializedType, ListResponseType} from 'sweet-types';

export const LANE_RTK_TAGS = {
  TAGS: 'lane',
};

export const extendedAPI = sweetAPI
  .enhanceEndpoints({addTagTypes: [LANE_RTK_TAGS.TAGS]})
  .injectEndpoints({
    endpoints: (build) => ({
      lanes: build.query<
        ListResponseType<LaneSerializedType>,
        | void
        | CommonFetchParams
        | ({
            object_ids: number | string;
            content_type:
              | 'procedure'
              | 'procedureversion'
              | 'proceduregroup'
              | 'processversion';
          } & CommonFetchParams)
      >({
        query: (params = {}) => ({
          url: LANES_API,
          params: {page: 1, ...params},
        }),
        providesTags: (response) =>
          createTagsListFromResponse(LANE_RTK_TAGS.TAGS, response, {
            addsListId: true,
            idGetter: ({hashid}) => hashid,
          }),
      }),
      lanesForDocument: build.query<
        ListResponseType<LaneSerializedType>,
        {
          object_ids: number | string;
          content_type:
            | 'procedure'
            | 'procedureversion'
            | 'proceduregroup'
            | 'processversion';
        }
      >({
        query: ({object_ids, content_type}) => ({
          url: LANES_API,
          params: {page_size: UNLIMITED_PAGE_SIZE, object_ids, content_type},
        }),
        providesTags: (response) =>
          createTagsListFromResponse(LANE_RTK_TAGS.TAGS, response, {
            addsListId: true,
            idGetter: ({hashid}) => hashid,
          }),
        onQueryStarted: async (args, {queryFulfilled, ...api}) => {
          queryFulfilled.catch((result) => {
            captureTokenAuthError(result.error as any, api, [
              'required-not-in-scope',
            ]);
          });
        },
      }),
      createLane: build.mutation<LaneSerializedType, CreateLaneSerializedType>({
        query: ({name}) => ({
          url: LANES_API,
          method: 'POST',
          body: {name},
        }),
        onQueryStarted: async (args, api) => {
          showMessages({
            onError: () => gt("Couldn't create the lane at the moment"),
          })(args, api);

          api.queryFulfilled.catch((result) => {
            captureTokenAuthError(result.error as any, api, [
              'required-not-in-scope',
            ]);
          });
        },
        invalidatesTags: (lane) => [
          {type: LANE_RTK_TAGS.TAGS, id: 'list'},
          {type: LANE_RTK_TAGS.TAGS, id: lane?.hashid ?? 0},
        ],
      }),
      updateLane: build.mutation<
        LaneSerializedType,
        {hashid: string; name: string}
      >({
        query: ({hashid, name}) => ({
          url: `${LANES_API}${hashid}/`,
          method: 'PATCH',
          body: {name},
        }),
        onQueryStarted: showMessages({
          onError: () => gt("Couldn't update the lane at the moment"),
        }),
        invalidatesTags: (lane) => [
          {type: 'lane', id: 'list'},
          {type: 'lane', id: lane?.hashid ?? 0},
        ],
      }),
      deleteLane: build.mutation<unknown, string>({
        query: (hashid) => ({
          url: `${LANES_API}${hashid}/`,
          method: 'DELETE',
        }),
        onQueryStarted: showMessages({
          onError: () => gt("Couldn't move the lane to the Recycle bin"),
          onSuccess: () => gt('Lane moved to Recycle Bin'),
        }),
        invalidatesTags: () => [{type: 'lane', id: 'list'}],
      }),
    }),
  });

// https://redux-toolkit.js.org/rtk-query/usage/queries#hook-types
export const {
  useLanesQuery: useLanes,
  useLanesForDocumentQuery: useLanesForDocument,
  useCreateLaneMutation: useCreateLane,
  useUpdateLaneMutation: useUpdateLane,
  useDeleteLaneMutation: useDeleteLane,
} = extendedAPI;
